import React, { useEffect, useState } from 'react';
import { TextField, Box, IconButton, TableCellProps, Typography, InputAdornment, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@shared/icons';

interface JSEditableInputProps extends TableCellProps {
  value: number | string | undefined;
  onValueChange: (value: string) => void;
  placeholder?: string;
  suffix?: string;
  asDiv?: boolean;
  outlined?: boolean;
  label?: string;
  type?: 'number' | 'text';
}

const JSEditableInput: React.FC<JSEditableInputProps> = ({
  value,
  onValueChange,
  placeholder,
  suffix = '',
  asDiv = false,
  sx,
  outlined = false,
  type = 'number',
  ...props
}) => {

  const theme = useTheme();

  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value?.toString() || '');
  const [showEditIcon, setShowEditIcon] = useState(false);

  useEffect(() => {
    setInputValue(value?.toString() || '');
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event?.stopPropagation();
    event?.preventDefault();
    setIsEditing(true);
  };

  const commitAndExit = () => {
    onValueChange(inputValue);
    setIsEditing(false);
    setShowEditIcon(false);
  };

  const handleAccept = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    event?.preventDefault();
    commitAndExit();
  };

  const handleDecline = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    event?.preventDefault();
    setInputValue(value?.toString() || '');
    setIsEditing(false);
    setShowEditIcon(false);
  };

  const handleTextFieldBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const next = e.relatedTarget as HTMLElement | null;
    if (next?.closest?.('[data-jseditable-accept], [data-jseditable-cancel]')) {
      return;
    }
    commitAndExit();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAccept();
    } else if (event.key === 'Escape') {
      handleDecline();
    }
  };

  const content = (
    <Box
      onClick={handleClick}
      onMouseOver={() => setShowEditIcon(true)}
      onMouseOut={() => setShowEditIcon(false)}
      sx={{
        backgroundColor: outlined ? theme.palette.background.default : 'transparent',
        minWidth: 100,
        p: 1,
        border: outlined ? `1px solid ${theme.palette.divider}` : 'none',
        borderRadius: 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        height: "40px",
        boxSizing: "border-box",
        cursor: 'text',
        '&:hover': {
          opacity: 0.8
        },
        ...sx
      }}
    >

      <Typography variant="body2" sx={{
        flex: 1,
      }} noWrap>
        {inputValue ? `${inputValue} ${suffix}` : placeholder}
      </Typography>
      {showEditIcon && <Icon icon="IcEdit" />}
    </Box>
  );

  const editingContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, position: 'relative', ...sx }}>
      <TextField
        size="small"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={handleTextFieldBlur}
        onKeyDown={handleKeyPress}
        type={type}
        placeholder={placeholder}
        autoFocus
        InputProps={{
          endAdornment: <InputAdornment position="end">{suffix}</InputAdornment>
        }}
      />
      <Box sx={{

        display: 'flex', position: 'absolute', right: 0, bottom: -30, gap: 0.5
      }}>
        <IconButton
          size="small"
          data-jseditable-accept
          onClick={handleAccept}
          color="success"
          sx={{ p: 1, backgroundColor: 'white', borderRadius: 2, boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)' }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          data-jseditable-cancel
          onClick={handleDecline}
          color="error"
          sx={{ p: 1, backgroundColor: 'white', borderRadius: 2, boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  if (asDiv) {
    return isEditing ? editingContent : content;
  }

  return (
    <>
      {isEditing ? editingContent : content}
    </>
  );
};

export default JSEditableInput; 